import React, { useMemo } from "react";
import classNames from "classnames/bind";
import { Select } from "@/components/__shared__/Select/Select";
import { SizeError } from "./components/SizeError/SizeError";
import { Size as SizeType } from "./types";
import styles from "./SizeDropDown.module.scss";
import sizeStyles from "@/components/__shared__/SizeList/Size.module.scss";

const cx = classNames.bind(styles);

type SizeDropDownProps = {
  uniqueSizes: SizeType[];
  /**
   * A function that handles click events, taking a string value as an argument.
   */
  handleClick: (value: string) => void;
  /* A flag for using optgroups within the select list */
  isGroupingAllowed?: boolean;
  FitToolAndSizeChartComponent: React.FC<React.HTMLAttributes<HTMLDivElement>>;
  withFitToolAndSizeChart: boolean;
  ref: React.RefObject<HTMLSelectElement | null>;
};

export const SizeDropDownUI = ({
  uniqueSizes,
  handleClick,
  isGroupingAllowed = false,
  FitToolAndSizeChartComponent,
  withFitToolAndSizeChart,
  ref,
}: SizeDropDownProps) => {
  let currentSize = uniqueSizes.find((size) => size.isSelected)?.name || "";
  const [sizeValue, setSizeValue] = React.useState(currentSize);

  // If we've switched from a specific colour/size variant to another colour without that same size,
  // we need to update the current size value otherwise the UI will still display the old one
  if (currentSize !== sizeValue) {
    setSizeValue(currentSize);
  }

  const OUT_OF_STOCK = " - Out of stock";

  const generateSizeLabel = (size: SizeType) => {
    const isOutOfStock = size.unavailable;
    const isSizeReducedToClear = size.showReduced;

    return `${size.name} ${isOutOfStock ? OUT_OF_STOCK : ""} ${
      isSizeReducedToClear && !isOutOfStock
        ? `- ${size.sizeReductionLabel}`
        : ""
    }`;
  };

  const groupedSizes = useMemo(() => {
    const groupedSizes: Record<string, SizeType[]> = {};

    uniqueSizes.forEach((size) => {
      groupedSizes[size.sizeHeadline] = [
        ...(groupedSizes[size.sizeHeadline] || []),
        size,
      ];
    });

    return groupedSizes;
  }, [uniqueSizes]);

  const isGrouped = isGroupingAllowed && Object.keys(groupedSizes).length > 1;

  if (uniqueSizes.length == 0) {
    return null;
  }

  const sizeOptions = uniqueSizes.map((size) => {
    return {
      label: generateSizeLabel(size),
      value: size.name,
    };
  });

  const options = [{ label: "Select a size", value: "" }];
  if (!isGrouped) options.push(...sizeOptions);

  const optgroup = [];
  if (isGrouped) {
    const groupedOptions = Object.entries(groupedSizes).map(
      ([groupName, sizes]) => ({
        label: groupName,
        options: sizes.map((size) => {
          return { label: generateSizeLabel(size), value: size.name };
        }),
      }),
    );
    optgroup.push(...groupedOptions);
  }

  return (
    <div data-component="SizeDropDown">
      <SizeError
        sizes={uniqueSizes.map((size) => ({
          name: size.name,
        }))}
      ></SizeError>
      <h2 id="select-size-heading" className={styles.heading}>
        <span
          aria-hidden="true"
          data-cy="size-heading"
          className={styles.size_text}
        >
          <strong
            data-cy="size-heading-text"
            className={styles["size_heading_text"]}
          >
            Size:&nbsp;
          </strong>
          {sizeValue ? (
            <p className={styles["size_value_text"]}>{sizeValue}</p>
          ) : (
            ""
          )}
        </span>
      </h2>
      {withFitToolAndSizeChart && (
        <FitToolAndSizeChartComponent className={styles.fitToolAndSizeChart} />
      )}
      {uniqueSizes.length > 1 ? (
        <Select
          ref={ref}
          id="select-size"
          name="select-size"
          dataTestId="select-size"
          ariaLabel="Select the size"
          onChange={(e: { target: HTMLSelectElement }) => {
            setSizeValue(e.target.value);
            handleClick(e.target.value);
            currentSize = e.target.value;
          }}
          value={sizeValue}
        >
          {options.map((option) => (
            <option value={option.value} key={option.label}>
              {option.label}
            </option>
          ))}
          {optgroup.map((optgroupItem) => (
            <optgroup key={optgroupItem.label} label={optgroupItem.label}>
              {optgroupItem.options.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </optgroup>
          ))}
        </Select>
      ) : (
        <div className={cx(sizeStyles.sizeItem, sizeStyles.sizeItem__three)}>
          <button
            disabled
            type="button"
            className={cx(
              sizeStyles.size__label,
              sizeStyles.size__label__selected,
            )}
          >
            {sizeValue}
          </button>
        </div>
      )}
    </div>
  );
};
